/**
 * API Client Service
 * 
 * Communicates with FastAPI backend
 */

import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for session auth
});

export const CSRF_COOKIE_NAME = 'smartdeploy_csrf';
export const CSRF_HEADER_NAME = 'X-CSRF-Token';

let csrfTokenOverride = '';
let csrfRefreshPromise = null;

export const getCookieValue = (name) => {
  if (typeof document === 'undefined') return '';
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')
    .slice(1)
    .join('=') || '';
};

export const getCsrfToken = () => csrfTokenOverride || decodeURIComponent(getCookieValue(CSRF_COOKIE_NAME) || '');

export const csrfHeaders = (headers = {}) => {
  const token = getCsrfToken();
  return token ? { ...headers, [CSRF_HEADER_NAME]: token } : headers;
};

export const rememberCsrfToken = (token) => {
  csrfTokenOverride = token || '';
};

export const ensureCsrfToken = async () => {
  const existingToken = getCsrfToken();
  if (existingToken) return existingToken;

  if (!csrfRefreshPromise) {
    csrfRefreshPromise = api.get('/auth/csrf')
      .then((response) => {
        const token = response.data?.csrf_token || '';
        rememberCsrfToken(token);
        return token;
      })
      .finally(() => {
        csrfRefreshPromise = null;
      });
  }
  return csrfRefreshPromise;
};

const setRequestHeader = (headers, name, value) => {
  if (typeof headers.set === 'function') {
    headers.set(name, value);
  } else {
    headers[name] = value;
  }
};

export const applyCsrfHeader = (config, tokenOverride = null) => {
  const token = tokenOverride || getCsrfToken();
  if (token) {
    config.headers = config.headers || {};
    setRequestHeader(config.headers, CSRF_HEADER_NAME, token);
  }
  return config;
};

export const csrfFetch = (url, options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  const unsafe = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  return fetch(url, {
    ...options,
    credentials: options.credentials || 'include',
    headers: unsafe ? csrfHeaders(options.headers || {}) : options.headers,
  });
};

const isUnsafeMethod = (method = 'get') => ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
const CSRF_BOOTSTRAP_EXEMPT_PATHS = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
]);

const requestPath = (url = '') => {
  try {
    return new URL(url, api.defaults.baseURL).pathname.replace(/^\/api\/v1/, '');
  } catch {
    return String(url).split('?')[0];
  }
};

const extractErrorDetail = async (data) => {
  if (!data) return undefined;
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)?.detail || data;
    } catch {
      return data;
    }
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    const text = await data.text();
    if (!text) return undefined;
    try {
      return JSON.parse(text)?.detail || text;
    } catch {
      return text;
    }
  }
  return undefined;
};

api.interceptors.request.use(async (config) => {
  if (isUnsafeMethod(config.method)) {
    let token = getCsrfToken();
    if (!token && !CSRF_BOOTSTRAP_EXEMPT_PATHS.has(requestPath(config.url))) {
      token = await ensureCsrfToken();
    }
    applyCsrfHeader(config, token);
  }
  return config;
});

// ──────────── Cookie-Only Auth (BFF Pattern) ────────────
// Auth is managed exclusively via HttpOnly session cookies.
// The withCredentials: true setting ensures cookies are sent on every request.
// localStorage token storage has been removed to eliminate XSS token theft risk.

/**
 * No-op — retained for backward compatibility.
 * Session is managed via HttpOnly cookie set by the backend.
 * @deprecated Tokens are no longer stored client-side.
 */
export const storeSessionToken = (_token) => {
  // No-op: BFF pattern — cookie is set by Set-Cookie header
};

/**
 * No-op — retained for backward compatibility.
 * @deprecated Tokens are no longer stored client-side.
 */
export const clearSessionToken = () => {
  rememberCsrfToken('');
};

// Response interceptor: redirect to login on 401 (session expired / invalid)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const detail = await extractErrorDetail(error.response?.data);
    const isCsrfError = error.response?.status === 403
      && typeof detail === 'string'
      && detail.toLowerCase().includes('csrf token');

    if (
      isCsrfError
      && originalRequest
      && isUnsafeMethod(originalRequest.method)
      && !originalRequest._csrfRetry
    ) {
      originalRequest._csrfRetry = true;
      const csrfResponse = await api.get('/auth/csrf');
      const freshToken = csrfResponse.data?.csrf_token;
      rememberCsrfToken(freshToken);
      applyCsrfHeader(originalRequest, freshToken);
      return api(originalRequest);
    }

    if (error.response?.status === 401 && !originalRequest?.skipAuthRedirect) {
      // Redirect to login if not already there
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ──────────── In-flight Deduplication & TTL Cache ────────────
// Prevents redundant concurrent GET requests and caches responses for rapid navigation
const _inflightRequests = new Map(); // key → Promise
const _responseCache = new Map();    // key → { data, timestamp }
const CACHE_TTL_MS = 30_000;         // 30 seconds

/**
 * Wraps api.get with deduplication + TTL caching.
 * - Concurrent identical GETs share a single network request
 * - Responses are cached for CACHE_TTL_MS to speed up back/forward navigation
 */
const cachedGet = (url, config = {}) => {
  const cacheKey = url + (config.params ? JSON.stringify(config.params) : '');

  // Check TTL cache first
  const cached = _responseCache.get(cacheKey);
  if (cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS) {
    return Promise.resolve(cached.data);
  }

  // Check in-flight deduplication
  if (_inflightRequests.has(cacheKey)) {
    return _inflightRequests.get(cacheKey);
  }

  // Make new request
  const promise = api.get(url, config)
    .then(response => {
      // Cache the response
      _responseCache.set(cacheKey, { data: response, timestamp: Date.now() });
      _inflightRequests.delete(cacheKey);
      return response;
    })
    .catch(err => {
      _inflightRequests.delete(cacheKey);
      throw err;
    });

  _inflightRequests.set(cacheKey, promise);
  return promise;
};

/**
 * Invalidate cache entries matching a URL prefix.
 * Called automatically after mutating operations.
 */
const invalidateCache = (urlPrefix) => {
  for (const key of _responseCache.keys()) {
    if (key.startsWith(urlPrefix)) {
      _responseCache.delete(key);
    }
  }
};

// Auto-invalidate cache on mutating operations
api.interceptors.response.use(
  (response) => {
    const method = response.config.method?.toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      // Extract the resource path prefix (e.g., "/projects/123")
      const url = response.config.url || '';
      const parts = url.split('/').slice(0, 3).join('/'); // e.g., "/projects/123"
      invalidateCache(parts);
    }
    return response;
  },
  (error) => Promise.reject(error)
);


// Analysis endpoints
export const analysisAPI = {
  // List available modules
  listModules: (organizationId = null) =>
    api.get('/analysis/modules', {
      params: organizationId ? { organization_id: organizationId } : {}
    }),

  // Get module details
  getModule: (moduleId) => api.get(`/analysis/modules/${moduleId}`),

  // Run analysis
  runAnalysis: (moduleId, routeGeoJSON, parameters) =>
    api.post('/analysis/run', {
      module_id: moduleId,
      route_geojson: routeGeoJSON,
      parameters: parameters
    }),

  // Ingest a zipped shapefile and return GeoJSON
  ingestShapefile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/analysis/ingest/shapefile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Ingest a GeoJSON from a URL and return normalized GeoJSON
  ingestGeojsonUrl: (url) =>
    api.post('/analysis/ingest/geojson-url', { url }),
};

// Pricing / billing-code endpoints
export const pricingAPI = {
  ingest: (kind, file, organizationId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    // kind + organization_id are query params to keep multipart simple
    // Note: Don't set Content-Type header - axios will set it with boundary automatically
    return api.post('/pricing/ingest', formData, {
      params: {
        kind,
        ...(organizationId ? { organization_id: organizationId } : {}),
      },
    });
  },

  estimate: (uploadId, organizationId = null) =>
    api.post('/pricing/estimate', {
      upload_id: uploadId,
      ...(organizationId ? { organization_id: organizationId } : {}),
    }),
};

// Auth endpoints
export const authAPI = {
  login: (email, password) =>
    api.post('/auth/login', { email, password }),

  register: (email, password, name) =>
    api.post('/auth/register', { email, password, name }),

  logout: () =>
    api.post('/auth/logout'),

  getMe: (config = {}) =>
    api.get('/auth/me', config),

  updateProfile: (data) =>
    api.put('/auth/profile', data),

  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    }),

  forgotPassword: (email) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', {
      token,
      new_password: newPassword
    }),

  refreshCsrf: () =>
    api.get('/auth/csrf'),
};

// Project endpoints
export const projectAPI = {
  // Create a new project
  createProject: (name, description, moduleId, programId, inputGeojson, parameters, results) =>
    api.post('/projects', {
      name,
      description,
      module_id: moduleId,
      program_id: programId,
      input_geojson: inputGeojson,
      parameters,
      results
    }),

  // List all projects for current user
  listProjects: (programId = null, organizationId = null, search = null) =>
    cachedGet('/projects', {
      params: {
        ...(programId !== null ? { program_id: programId } : {}),
        ...(organizationId !== null ? { organization_id: organizationId } : {}),
        ...(search ? { search } : {})
      }
    }),

  // Get a specific project
  getProject: (projectId) =>
    cachedGet(`/projects/${projectId}`),

  // Update a project
  updateProject: (projectId, updates) =>
    api.put(`/projects/${projectId}`, updates),

  // Delete a project
  deleteProject: (projectId) =>
    api.delete(`/projects/${projectId}`),

  // Update project lifecycle status
  updateProjectStatus: (projectId, status) =>
    api.patch(`/projects/${projectId}/status`, { status }),

  // Get jobs for a project
  getProjectJobs: (projectId, limit = null, offset = 0) =>
    api.get(`/projects/${projectId}/jobs`, {
      params: {
        ...(limit !== null ? { limit } : {}),
        ...(offset ? { offset } : {})
      }
    }),

  getProjectJobsSummary: (projectId) =>
    api.get(`/projects/${projectId}/jobs-summary`),

  // Check for overlapping projects
  checkOverlap: (inputGeojson, organizationId) =>
    api.post('/projects/check-overlap', {
      input_geojson: inputGeojson,
      organization_id: organizationId
    }),

  // Recalculate project risk
  recalculateRisk: (projectId) =>
    api.post(`/projects/${projectId}/recalculate-risk`),

  // Get risk layers for heat map visualization
  getRiskLayers: (projectId) =>
    api.get(`/projects/${projectId}/risk-layers`),

  // Get risk heatmap points for density visualization
  getRiskHeatmap: (projectId) =>
    api.get(`/projects/${projectId}/risk-heatmap`),

  // Recalculate permit cost with excluded permit types
  recalculatePermitCost: (projectId, excludedPermitTypes) =>
    api.post(`/projects/${projectId}/recalculate-permit-cost`, {
      excluded_permit_types: excludedPermitTypes
    }),

  // Update market analysis data
  updateMarketData: (projectId, marketData) =>
    api.put(`/projects/${projectId}/market-data`, marketData),

  // Auto-populate market data from external APIs and analysis results
  autoPopulateMarketData: (projectId) =>
    api.post(`/projects/${projectId}/market-data/auto-populate`),

  getProjectReport: (projectId) =>
    api.get(`/projects/${projectId}/report`, {
      responseType: 'blob'
    }),

  // Export analysis PDFs and KMZs as ZIP
  exportAnalysisBundle: (projectId, jobIds = [], includePdf = true, includeKmz = true, includeProjectAssets = true, includeExecutiveOverview = false) =>
    api.post(`/projects/${projectId}/export/analysis-bundle`, {
      job_ids: jobIds,
      include_pdf: includePdf,
      include_kmz: includeKmz,
      include_project_assets: includeProjectAssets,
      include_executive_overview: includeExecutiveOverview
    }, { responseType: 'blob' }),

  startAnalysisBundleExport: (projectId, jobIds = [], includePdf = true, includeKmz = true, includeProjectAssets = true, includeExecutiveOverview = false) =>
    api.post(`/projects/${projectId}/export/analysis-bundle/tasks`, {
      job_ids: jobIds,
      include_pdf: includePdf,
      include_kmz: includeKmz,
      include_project_assets: includeProjectAssets,
      include_executive_overview: includeExecutiveOverview
    }),

  getAnalysisBundleExportTask: (projectId, taskId) =>
    api.get(`/projects/${projectId}/export/analysis-bundle/tasks/${taskId}`),

  downloadAnalysisBundleExport: (projectId, taskId) =>
    api.get(`/projects/${projectId}/export/analysis-bundle/tasks/${taskId}/download`, {
      responseType: 'blob'
    }),
};

// Program endpoints
export const programAPI = {
  // Create a new program
  createProgram: (name, description, organizationId = null, startDate = null, endDate = null, status = 'active') =>
    api.post('/programs', {
      name,
      description,
      organization_id: organizationId,
      start_date: startDate,
      end_date: endDate,
      status
    }),

  // List all programs for current user
  listPrograms: (organizationId = null) =>
    cachedGet('/programs', {
      params: {
        ...(organizationId !== null ? { organization_id: organizationId } : {})
      }
    }),

  // Get a specific program
  getProgram: (programId) =>
    cachedGet(`/programs/${programId}`),

  // Update a program
  updateProgram: (programId, updates) =>
    api.put(`/programs/${programId}`, updates),

  // Delete a program
  deleteProgram: (programId) =>
    api.delete(`/programs/${programId}`),

  // Create a project within a program
  createProject: (programId, name, description, inputGeojson = null) =>
    api.post('/projects', {
      program_id: programId,
      name,
      description: description || null,
      input_geojson: inputGeojson || {}
    }),

  // List projects for a program
  listProgramProjects: (programId, includeGeometry = true) =>
    api.get(`/programs/${programId}/projects`, {
      params: { include_geometry: includeGeometry }
    }),

  // One lightweight marker per project, derived from stored asset bounds
  getProgramMapSummary: (programId) =>
    api.get(`/programs/${programId}/map-summary`),

  // Generate program polygon from project centroids
  generateProgramPolygon: (programId, bufferFeet = 500) =>
    api.post(`/programs/${programId}/generate-polygon`, {
      buffer_feet: bufferFeet
    }),

  // Get program risk report (PDF)
  getProgramReport: (programId) =>
    api.get(`/programs/${programId}/report`, {
      responseType: 'blob'
    }),

  // Recalculate program risk (cascades to all projects)
  recalculateRisk: (programId) =>
    api.post(`/programs/${programId}/recalculate`),

  // Get all assets from all projects as combined GeoJSON
  getProgramAssetsGeoJSON: (programId) =>
    api.get(`/programs/${programId}/assets-geojson`),

  // Update program settings (cost rates, etc.)
  updateProgramSettings: (programId, settings) =>
    api.patch(`/programs/${programId}/settings`, settings)
};

// Program Pricing endpoints
export const programPricingAPI = {
  // Upload pricing file
  uploadPricing: (programId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/programs/${programId}/pricing/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // List uploads for a program
  listUploads: (programId) =>
    api.get(`/programs/${programId}/pricing/uploads`),

  // Get upload lines with optional filter
  getUploadLines: (programId, uploadPublicId, reviewStatus = null) =>
    api.get(`/programs/${programId}/pricing/uploads/${uploadPublicId}/lines`, {
      params: reviewStatus ? { review_status: reviewStatus } : {}
    }),

  // Approve a line match
  approveLine: (programId, uploadPublicId, lineId) =>
    api.post(`/programs/${programId}/pricing/review/${lineId}/approve`),

  // Reject a line match
  rejectLine: (programId, uploadPublicId, lineId, notes = null) =>
    api.post(`/programs/${programId}/pricing/review/${lineId}/reject`, {
      notes
    }),

  // Select an alternative match
  selectMatch: (programId, lineId, canonicalCodeId) =>
    api.patch(`/programs/${programId}/pricing/lines/${lineId}/select-match`, {
      canonical_code_id: canonicalCodeId
    }),

  // Get approved rate sheet for program
  getApprovedRateSheet: (programId) =>
    api.get(`/programs/${programId}/pricing/approved`),

  // Create new canonical code from rejected line
  createCanonicalCode: (canonicalCode, name, category, defaultUom, description = null) =>
    api.post('/canonical-codes', {
      canonical_code: canonicalCode,
      name,
      category,
      default_uom: defaultUom,
      description
    }),

  // Cancel a processing upload
  cancelUpload: (programId, uploadPublicId) =>
    api.post(`/programs/${programId}/pricing/uploads/${uploadPublicId}/cancel`),

  // Delete an upload and all its lines
  deleteUpload: (programId, uploadPublicId) =>
    api.delete(`/programs/${programId}/pricing/uploads/${uploadPublicId}`)
};

// ROW Permitting endpoints
export const rowPermittingAPI = {
  listCorridors: (projectId = null) =>
    api.get('/permits/row/corridors', {
      params: { ...(projectId ? { project_id: projectId } : {}) },
    }),
  createCorridor: (payload) => api.post('/permits/row/corridors', payload),
  deleteCorridor: (corridorId) => api.delete(`/permits/row/corridors/${corridorId}`),
  importFromInference: (corridorId) => api.post(`/permits/row/corridors/${corridorId}/import-from-inference`),
  dashboard: () => api.get('/permits/row/dashboard'),
  listAgencies: () => api.get('/permits/row/agencies'),
  createAgency: (payload) => api.post('/permits/row/agencies', payload),
  updateAgency: (agencyId, payload) => api.put(`/permits/row/agencies/${agencyId}`, payload),
  listRequirements: (projectId = null) =>
    api.get('/permits/row/requirements', {
      params: { ...(projectId ? { project_id: projectId } : {}) },
    }),
  createRequirement: (payload) => api.post('/permits/row/requirements', payload),
  convertRequirement: (requirementId) => api.post(`/permits/row/requirements/${requirementId}/convert-to-active-permit`),
  getRequirementChecklist: (requirementId) => api.get(`/permits/row/requirements/${requirementId}/checklist`),
  updateChecklistItem: (requirementId, itemId, payload) =>
    api.patch(`/permits/row/requirements/${requirementId}/checklist/${itemId}`, payload),
  generateSubmissionEmails: (corridorId) => api.post(`/permits/row/corridors/${corridorId}/generate-submission-emails`),
  listRecordsRequests: (projectId = null) =>
    api.get('/permits/row/records-requests', {
      params: { ...(projectId ? { project_id: projectId } : {}) },
    }),
  createRecordsRequest: (payload) => api.post('/permits/row/records-requests', payload),
  updateRecordsRequestStatus: (requestId, status, reason = null, note = null) =>
    api.patch(`/permits/row/records-requests/${requestId}/status`, { status, reason, note }),
  sendRecordsRequestEmail: (requestId, payload) => api.post(`/permits/row/records-requests/${requestId}/send-email`, payload),
  listActivePermits: (projectId = null) =>
    api.get('/permits/row/active-permits', {
      params: { ...(projectId ? { project_id: projectId } : {}) },
    }),
  createActivePermit: (payload) => api.post('/permits/row/active-permits', payload),
  updateActivePermitStatus: (permitId, status, reason = null, note = null) =>
    api.patch(`/permits/row/active-permits/${permitId}/status`, { status, reason, note }),
  sendActivePermitEmail: (permitId, payload) => api.post(`/permits/row/active-permits/${permitId}/send-email`, payload),
  ingestCommunications: (mailboxId = 'shared-row-mailbox') =>
    api.post(`/permits/row/communications/ingest?mailbox_id=${encodeURIComponent(mailboxId)}`),
  communicationsQueue: () => api.get('/permits/row/communications/queue'),
  associateCommunication: (threadId, recordType, recordId) =>
    api.post(`/permits/row/communications/${threadId}/associate`, { record_type: recordType, record_id: recordId }),
  markInformational: (threadId) => api.post(`/permits/row/communications/${threadId}/mark-informational`),
};

// Project Pricing Override endpoints
export const projectPricingAPI = {
  // Get current pricing overrides and effective rates
  getOverrides: (projectId) =>
    api.get(`/projects/${projectId}/pricing-overrides`),

  // Update project-level pricing overrides
  updateOverrides: (projectId, pricingOverrides) =>
    api.put(`/projects/${projectId}/pricing-overrides`, {
      pricing_overrides: pricingOverrides
    }),
};

// Organization endpoints
export const organizationAPI = {
  // Create new organization
  createOrganization: (name, description, ownerUserId = null, accountType = 'standalone', parentAccountId = null) =>
    api.post('/organizations', {
      name,
      description,
      account_type: accountType,
      ...(ownerUserId ? { owner_user_id: ownerUserId } : {}),
      ...(parentAccountId ? { parent_account_id: parentAccountId } : {})
    }),

  // List user's organizations
  listOrganizations: () =>
    api.get('/organizations'),

  // Get organization details
  getOrganization: (orgId) =>
    api.get(`/organizations/${orgId}`),

  // Update organization
  updateOrganization: (orgId, updates) =>
    api.put(`/organizations/${orgId}`, updates),

  // Delete organization
  deleteOrganization: (orgId) =>
    api.delete(`/organizations/${orgId}`),

  // Member management
  inviteMember: (orgId, email, role) =>
    api.post(`/organizations/${orgId}/members`, { email, role }),

  // External API keys
  listApiKeys: (orgId) =>
    api.get(`/organizations/${orgId}/api-keys`),

  createApiKey: (orgId, name) =>
    api.post(`/organizations/${orgId}/api-keys`, { name }),

  revokeApiKey: (orgId, keyId) =>
    api.delete(`/organizations/${orgId}/api-keys/${keyId}`),

  // Organization AI routing
  getAIConfig: (orgId) =>
    api.get(`/organizations/${orgId}/ai-config`),

  updateAIConfig: (orgId, config) =>
    api.put(`/organizations/${orgId}/ai-config`, config),

  testAIConfig: (orgId) =>
    api.post(`/organizations/${orgId}/ai-config/test`),

  deleteAIConnection: (orgId, provider) =>
    api.delete(`/organizations/${orgId}/ai-connections/${provider}`),

  listExactixKeys: (orgId) =>
    api.get(`/organizations/${orgId}/exactix-key`),

  generateExactixKey: (orgId) =>
    api.post(`/organizations/${orgId}/exactix-key`),

  // Subcontractor ExactTix keys
  createSubcontractorExactixKey: (orgId, subcontractorName, contactEmail) =>
    api.post(`/organizations/${orgId}/exactix-keys/subcontractor`, {
      subcontractor_name: subcontractorName,
      contact_email: contactEmail || null,
    }),

  listSubcontractorExactixKeys: (orgId) =>
    api.get(`/organizations/${orgId}/exactix-keys/subcontractors`),

  regenerateExactixEmailTemplate: (orgId, keyId) =>
    api.post(`/organizations/${orgId}/exactix-keys/${keyId}/email-template`),

  revokeSubcontractorExactixKey: (orgId, keyId) =>
    api.delete(`/organizations/${orgId}/exactix-keys/subcontractor/${keyId}`),

  listMembers: (orgId) =>
    api.get(`/organizations/${orgId}/members`),

  updateMemberRole: (orgId, userId, role) =>
    api.put(`/organizations/${orgId}/members/${userId}`, { role }),

  removeMember: (orgId, userId) =>
    api.delete(`/organizations/${orgId}/members/${userId}`),

  // Transfer ownership
  transferOwnership: (orgId, newOwnerUserId) =>
    api.post(`/organizations/${orgId}/transfer-ownership`, { new_owner_user_id: newOwnerUserId }),

  // Accept invitation
  acceptInvitation: (token) =>
    api.post(`/organizations/invitations/${token}/accept`),

  // Join request flow
  requestJoin: (organizationId, organizationName, message) =>
    api.post('/organizations/join-requests', {
      organization_id: organizationId,
      organization_name: organizationName,
      message
    }),

  listJoinRequests: (orgId) =>
    api.get(`/organizations/${orgId}/join-requests`),

  approveJoinRequest: (orgId, requestId, role) =>
    api.post(`/organizations/${orgId}/join-requests/${requestId}/approve`, { role }),

  rejectJoinRequest: (orgId, requestId) =>
    api.post(`/organizations/${orgId}/join-requests/${requestId}/reject`),

  // Risk settings
  getRiskSettings: (orgId) =>
    api.get(`/organizations/${orgId}/risk-settings`),

  updateRiskSettings: (orgId, weights) =>
    api.put(`/organizations/${orgId}/risk-settings`, { weights }),

  // Organization branding settings (app shell, email, and reports)
  getBrandingSettings: (orgId) =>
    api.get(`/organizations/${orgId}/branding-settings`),

  updateBrandingSettings: (orgId, settings) =>
    api.put(`/organizations/${orgId}/branding-settings`, settings),

  // Upload branding logo
  uploadBrandingLogo: (orgId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/organizations/${orgId}/branding-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Pricing Defaults
  getPricingDefaults: (orgId) =>
    api.get(`/organizations/${orgId}/pricing-defaults`),

  updatePricingDefaults: (orgId, pricingData) =>
    api.put(`/organizations/${orgId}/pricing-defaults`, pricingData),

  // User Management
  inviteUser: (orgId, userData) =>
    api.post(`/organizations/${orgId}/invite`, userData),

  getUserStats: (orgId) =>
    api.get(`/organizations/${orgId}/users/stats`),

  getAvailableUsers: (orgId) =>
    api.get(`/organizations/${orgId}/available-users`),

  addExistingUser: (orgId, userId, role) =>
    api.post(`/organizations/${orgId}/users/existing`, {
      user_id: userId,
      role
    }),

  getMembers: (orgId) =>
    api.get(`/organizations/${orgId}/members`),

  resendMemberInvite: (orgId, userId) =>
    api.post(`/organizations/${orgId}/members/${userId}/resend-invite`),

  sendMemberPasswordReset: (orgId, userId) =>
    api.post(`/organizations/${orgId}/members/${userId}/send-password-reset`),

  // Master Canonical Codes Upload
  uploadCanonicalCodes: (orgId, file) => {
    console.log('[DEBUG] uploadCanonicalCodes called with file:', file);
    const formData = new FormData();
    formData.append('file', file);
    console.log('[DEBUG] FormData created, entries:', Array.from(formData.entries()));

    // Don't override headers - let axios auto-detect FormData and set correct Content-Type with boundary
    // This works because axios detects FormData instances and handles them specially
    const config = {
      transformRequest: [(data, headers) => {
        // Remove the default Content-Type header to let browser set it with boundary
        delete headers['Content-Type'];
        return data;
      }]
    };

    return api.post(`/organizations/${orgId}/canonical-codes/upload`, formData, config);
  },

  // Deployment Centers
  getDeploymentCenters: (orgId) =>
    api.get(`/organizations/${orgId}/deployment-centers`),

  createDeploymentCenter: (orgId, data) =>
    api.post(`/organizations/${orgId}/deployment-centers`, data),

  updateDeploymentCenter: (orgId, centerId, data) =>
    api.put(`/organizations/${orgId}/deployment-centers/${centerId}`, data),

  deleteDeploymentCenter: (orgId, centerId) =>
    api.delete(`/organizations/${orgId}/deployment-centers/${centerId}`),

  // Salesforce Integration — Credentials (org-level)
  getSalesforceConfig: (orgId) =>
    api.get(`/organizations/${orgId}/salesforce-config`),

  updateSalesforceConfig: (orgId, config) =>
    api.put(`/organizations/${orgId}/salesforce-config`, config),

  testSalesforceConnection: (orgId) =>
    api.post(`/organizations/${orgId}/salesforce-config/test`),

  // Salesforce Integration — Object Introspection
  listSalesforceObjects: (orgId) =>
    api.post(`/organizations/${orgId}/salesforce-config/objects`),

  describeSalesforceObject: (orgId, objectName) =>
    api.post(`/organizations/${orgId}/salesforce-config/describe`, { object_name: objectName }),

  getLocalTables: (orgId) =>
    api.get(`/organizations/${orgId}/salesforce-config/local-tables`),

  getChildTables: (orgId) =>
    api.get(`/organizations/${orgId}/salesforce-config/child-tables`),

  getFieldValues: (orgId, objectName, fieldName) =>
    api.post(`/organizations/${orgId}/salesforce-config/field-values`, { object_name: objectName, field_name: fieldName }),

  // Salesforce Integration — Mapping CRUD
  getSalesforceMappings: (orgId) =>
    api.get(`/organizations/${orgId}/salesforce-mappings`),

  createSalesforceMapping: (orgId, data) =>
    api.post(`/organizations/${orgId}/salesforce-mappings`, data),

  updateSalesforceMapping: (orgId, mappingId, data) =>
    api.put(`/organizations/${orgId}/salesforce-mappings/${mappingId}`, data),

  deleteSalesforceMapping: (orgId, mappingId) =>
    api.delete(`/organizations/${orgId}/salesforce-mappings/${mappingId}`),

  syncSalesforceMapping: (orgId, mappingId) =>
    api.post(`/organizations/${orgId}/salesforce-mappings/${mappingId}/sync`),
};

// Asset endpoints
export const assetAPI = {
  // Create or update asset for a project (creates new version)
  createAsset: (projectId, assetType, name, description, geojson, changeNotes) =>
    api.post(`/projects/${projectId}/assets`, {
      asset_type: assetType,
      name,
      description,
      geojson,
      change_notes: changeNotes
    }),

  // List assets for a project
  listAssets: (projectId, assetType = null, activeOnly = true) =>
    cachedGet(`/projects/${projectId}/assets`, {
      params: {
        ...(assetType ? { asset_type: assetType } : {}),
        active_only: activeOnly
      }
    }),

  // Get specific asset with full GeoJSON
  getAsset: (projectId, assetId) =>
    api.get(`/projects/${projectId}/assets/${assetId}`),

  // Get combined GeoJSON of all active assets (bulk endpoint)
  getProjectAssetsGeoJSON: (projectId) =>
    cachedGet(`/projects/${projectId}/assets/geojson`),

  // Get version history for an asset type
  getAssetHistory: (projectId, assetType) =>
    api.get(`/projects/${projectId}/assets/type/${assetType}/history`),

  // Activate (rollback to) a specific version
  activateAsset: (projectId, assetId) =>
    api.post(`/projects/${projectId}/assets/${assetId}/activate`),

  // Update asset geometry (creates new version with updated geometry)
  updateAssetGeometry: (projectId, assetId, geometry) =>
    api.patch(`/projects/${projectId}/assets/${assetId}/geometry`, {
      geometry
    }),

  // Update entire asset GeoJSON (creates new version with updated GeoJSON)
  updateAssetGeoJSON: (projectId, assetId, geojson) =>
    api.patch(`/projects/${projectId}/assets/${assetId}/geojson`, {
      geojson
    }),

  // Delete an asset version
  deleteAsset: (projectId, assetId) =>
    api.delete(`/projects/${projectId}/assets/${assetId}`),

  // Delete all versions of an asset type (full removal)
  deleteAllVersions: (projectId, assetType) =>
    api.delete(`/projects/${projectId}/assets/type/${assetType}`),

  // --- Feature-level fielding endpoints ---
  listFeatures: (projectId, assetId) =>
    api.get(`/projects/${projectId}/assets/${assetId}/features`),
  // Batch: get all fieldable features in one request (replaces N+1 listFeatures calls)
  listAllFeatures: (projectId) =>
    api.get(`/projects/${projectId}/assets/all-features`),
  addFeature: (projectId, assetId, data) =>
    api.post(`/projects/${projectId}/assets/${assetId}/features`, data),
  updateFeature: (projectId, assetId, featureIndex, data) =>
    api.patch(`/projects/${projectId}/assets/${assetId}/features/${featureIndex}`, data),

  // Import asset from ArcGIS layer
  importFromEsri: (projectId, connectionId, layerUrl, assetType, name, description, changeNotes) =>
    api.post(`/projects/${projectId}/assets/from-esri`, {
      connection_id: connectionId,
      layer_url: layerUrl,
      asset_type: assetType,
      name,
      description,
      change_notes: changeNotes
    }),

  // Parse KMZ file to list layers
  parseKmz: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/assets/parse-kmz`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Import specific layers from KMZ/KML
  importKmzLayer: (projectId, layerNames, assetType, name, description, file) => {
    const formData = new FormData();
    // Pass as JSON string to handle list
    formData.append('layer_names', JSON.stringify(layerNames));
    formData.append('asset_type', assetType);
    formData.append('name', name);
    if (description) formData.append('description', description);
    formData.append('file', file);
    return api.post(`/projects/${projectId}/assets/import-kmz-layer`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Push HLD job results as versioned per-layer hld_ assets
  pushHLDToAssets: (projectId, jobId, name = null, description = null, changeNotes = null, force = false) => {
    // Explicitly cast all fields to safe primitives — guards against React event objects
    // or DOM elements accidentally reaching axios's JSON serializer.
    const body = {
      job_id: String(jobId),
      force: force === true,   // only ever true if caller explicitly passed true
    };
    if (name && typeof name === 'string') body.name = name;
    if (description && typeof description === 'string') body.description = description;
    if (changeNotes && typeof changeNotes === 'string') body.change_notes = changeNotes;
    return api.post(`/projects/${projectId}/assets/push-hld`, body);
  },



  // Push Landbase job results as per-layer versioned assets
  pushLandbaseToAssets: (projectId, jobId, changeNotes = null) =>
    api.post(`/projects/${projectId}/assets/push-landbase`, {
      job_id: jobId,
      ...(changeNotes ? { change_notes: changeNotes } : {})
    })
};

// ESRI 2-Way Sync endpoints
export const esriSyncAPI = {
  // Link management
  createLink: (projectId, connectionId, connectionScope, layerUrl, assetType, syncDirection, syncIntervalMinutes, fieldMapping) =>
    api.post('/esri-sync/links', {
      project_id: projectId,
      connection_id: connectionId,
      connection_scope: connectionScope,
      layer_url: layerUrl,
      asset_type: assetType,
      sync_direction: syncDirection,
      sync_interval_minutes: syncIntervalMinutes || null,
      field_mapping: fieldMapping || null,
    }),

  listLinks: (projectId) =>
    api.get('/esri-sync/links', { params: { project_id: projectId } }),

  updateLink: (linkId, updates) =>
    api.put(`/esri-sync/links/${linkId}`, updates),

  deleteLink: (linkId) =>
    api.delete(`/esri-sync/links/${linkId}`),

  // Sync operations
  triggerSync: (linkId) =>
    api.post(`/esri-sync/links/${linkId}/sync`),

  triggerPull: (linkId) =>
    api.post(`/esri-sync/links/${linkId}/pull`),

  triggerPush: (linkId) =>
    api.post(`/esri-sync/links/${linkId}/push`),

  // Conflicts
  getConflicts: (linkId) =>
    api.get(`/esri-sync/links/${linkId}/conflicts`),

  resolveConflicts: (linkId, resolutions) =>
    api.post(`/esri-sync/links/${linkId}/resolve`, { resolutions }),

  // Schema comparison
  getSchema: (linkId) =>
    api.get(`/esri-sync/links/${linkId}/schema`),
};

// Job endpoints
export const jobAPI = {
  // Create a background job - automatically uses active assets from project
  // Proper workflow: Organization -> Program -> Project -> Assets -> Job
  createJob: (moduleId, parameters, jobName, projectId, organizationId = null, requiredAssetTypes = null, assetIds = null) =>
    api.post('/jobs', {
      module_id: moduleId,
      parameters,
      job_name: jobName,
      project_id: projectId,  // Required
      organization_id: organizationId,
      ...(requiredAssetTypes ? { required_asset_types: requiredAssetTypes } : {}),
      ...(assetIds ? { asset_ids: assetIds } : {})
    }),

  // List all jobs for current user
  listJobs: (status = null, organizationId = null) =>
    api.get('/jobs', {
      params: {
        ...(status ? { status } : {}),
        ...(organizationId !== null ? { organization_id: organizationId } : {})
      }
    }),

  // Get jobs as map (GeoJSON with centroid points)
  getJobsMap: (organizationId = null, status = null) =>
    api.get('/jobs/map', {
      params: {
        ...(status ? { status } : {}),
        ...(organizationId !== null ? { organization_id: organizationId } : {})
      }
    }),

  // Get a specific job
  getJob: (jobId) =>
    api.get(`/jobs/${jobId}`),

  // Cancel a running job
  cancelJob: (jobId) =>
    api.post(`/jobs/${jobId}/cancel`),

  // Delete a job
  deleteJob: (jobId) =>
    api.delete(`/jobs/${jobId}`),

  // Share a job with an organization
  shareJobWithOrganization: (jobId, organizationId) =>
    api.put(`/jobs/${jobId}/share`, {
      organization_id: organizationId
    }),

  // Get results as GeoJSON
  getGeoJSON: (jobId) =>
    api.get(`/jobs/${jobId}/results/geojson`),

  // Publish results to ArcGIS
  publishToArcGIS: (jobId, data) =>
    api.post(`/jobs/${jobId}/publish-arcgis`, data),

  // Bulk job creation — runs multiple modules against a project
  createBulkJobs: (moduleIds, projectId, organizationId = null, assetIds = null, jobNamePrefix = null) =>
    api.post('/jobs/bulk', {
      module_ids: moduleIds,
      project_id: projectId,
      ...(organizationId ? { organization_id: organizationId } : {}),
      ...(assetIds ? { asset_ids: assetIds } : {}),
      ...(jobNamePrefix ? { job_name_prefix: jobNamePrefix } : {})
    }),

  // Poll aggregated status for a batch of bulk jobs
  getBulkStatus: (batchId) =>
    api.get(`/jobs/bulk/${batchId}/status`),
};

// Settings endpoints
export const settingsAPI = {
  // List Esri connections
  listConnections: (organizationId = null) =>
    api.get('/settings/esri-connections', {
      params: organizationId ? { organization_id: organizationId } : {}
    }),

  // Create Esri connection
  createConnection: (data) =>
    api.post('/settings/esri-connections', data),

  // Update Esri connection
  updateConnection: (connectionId, data) =>
    api.put(`/settings/esri-connections/${connectionId}`, data),

  // Delete Esri connection
  deleteConnection: (connectionId) =>
    api.delete(`/settings/esri-connections/${connectionId}`),

  // Test Esri connection
  testConnection: (connectionId) =>
    api.post(`/settings/esri-connections/${connectionId}/test`),

  // Set default connection
  setDefaultConnection: (connectionId) =>
    api.post(`/settings/esri-connections/${connectionId}/set-default`),

  // List services from a connection
  listConnectionServices: (connectionId, scope = 'org') =>
    api.get(`/settings/esri-connections/${connectionId}/services`, {
      params: { scope }
    }),

  // Search connection content (services and layers)
  searchConnectionContent: (connectionId, query = '', scope = 'both') =>
    api.get(`/settings/esri-connections/${connectionId}/search`, {
      params: { query, scope }
    }),

  // Get layers from a service
  getServiceLayers: (connectionId, serviceId, serviceUrl) =>
    api.get(`/settings/esri-connections/${connectionId}/services/${serviceId}/layers`, {
      params: { service_url: serviceUrl }
    }),

  // Get layer data as GeoJSON
  getLayerData: (connectionId, layerId, layerUrl) =>
    api.get(`/settings/esri-connections/${connectionId}/layers/${layerId}/data`, {
      params: { layer_url: layerUrl }
    })
};

// Billing & Subscription endpoints
export const billingAPI = {
  // Plans
  getPlans: () => api.get('/billing/plans'),

  // Subscriptions
  createSubscription: (planTier, billingInterval, organizationId = null, paymentMethodId = null, promoCode = null) =>
    api.post('/billing/subscriptions', {
      plan_tier: planTier,
      billing_interval: billingInterval,
      organization_id: organizationId,
      payment_method_id: paymentMethodId,
      promo_code: promoCode
    }),

  getCurrentSubscription: (organizationId = null) =>
    api.get('/billing/subscriptions/current', {
      params: organizationId ? { organization_id: organizationId } : {}
    }),

  cancelSubscription: (subscriptionId, cancelImmediately = false) =>
    api.post(`/billing/subscriptions/${subscriptionId}/cancel`, null, {
      params: { cancel_immediately: cancelImmediately }
    }),

  // Payment Methods
  createSetupIntent: (organizationId = null) =>
    api.post('/billing/payment-methods/setup-intent', null, {
      params: organizationId ? { organization_id: organizationId } : {}
    }),

  attachPaymentMethod: (paymentMethodId, setAsDefault = true, organizationId = null) =>
    api.post('/billing/payment-methods', {
      payment_method_id: paymentMethodId,
      set_as_default: setAsDefault,
      organization_id: organizationId
    }),

  listPaymentMethods: (organizationId = null) =>
    api.get('/billing/payment-methods', {
      params: organizationId ? { organization_id: organizationId } : {}
    }),

  removePaymentMethod: (paymentMethodId) =>
    api.delete(`/billing/payment-methods/${paymentMethodId}`),

  // Invoices
  listInvoices: (organizationId = null, limit = 10) =>
    api.get('/billing/invoices', {
      params: {
        ...(organizationId ? { organization_id: organizationId } : {}),
        limit
      }
    }),

  // Usage
  getUsageSummary: (organizationId = null) =>
    api.get('/billing/usage/summary', {
      params: organizationId ? { organization_id: organizationId } : {}
    }),

  getUsageDetails: (organizationId = null, limit = 100) =>
    api.get('/billing/usage/details', {
      params: {
        ...(organizationId ? { organization_id: organizationId } : {}),
        limit
      }
    }),
};

export const rateSheetAPI = {
  generate: (state = null, city = null, projectId = null, programId = null) =>
    api.get('/rate-sheets/generate', {
      params: {
        ...(state ? { state } : {}),
        ...(city ? { city } : {}),
        ...(projectId ? { project_id: projectId } : {}),
        ...(programId ? { program_id: programId } : {})
      }
    }),

  listLocations: () => api.get('/rate-sheets/locations'),
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (limit = 100) => api.get('/admin/users', { params: { limit } }),
  getOrganizations: (limit = 100) => api.get('/admin/organizations', { params: { limit } }),
  createOrganization: (data) => api.post('/admin/organizations', data),
  updateOrganization: (orgId, updates) => api.put(`/organizations/${orgId}`, updates),
  updateAccountHierarchy: (orgId, data) => api.patch(`/admin/organizations/${orgId}/hierarchy`, data),
  linkChildAccount: (parentOrgId, childAccountId, relationshipType = 'regional_child') =>
    api.post(`/admin/organizations/${parentOrgId}/children`, { child_account_id: childAccountId, relationship_type: relationshipType }),
  getProjects: (limit = 100) => api.get('/admin/projects', { params: { limit } }),
  getJobs: (limit = 50) => api.get('/admin/jobs', { params: { limit } }),
  getJobLogs: (jobId) => api.get(`/admin/jobs/${jobId}/logs`),
  createUser: (email, password, name) => api.post('/admin/users', { email, password, name }),
  resendUserInvite: (userId) => api.post(`/admin/users/${userId}/resend-invite`),
  sendUserPasswordReset: (userId) => api.post(`/admin/users/${userId}/send-password-reset`),
  updateUser: (userId, updates) => api.put(`/admin/users/${userId}`, updates),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  updateOrganizationSeats: (orgId, maxUsers) => api.patch(`/admin/organizations/${orgId}/seats`, { max_users: maxUsers }),
  updateLocateScraperLimit: (orgId, limit) => api.patch(`/admin/organizations/${orgId}/locate-scraper-limit`, { locate_scraper_limit: limit }),
  updateOrganizationStatus: (orgId, isActive) => api.patch(`/admin/organizations/${orgId}/status`, { is_active: isActive }),
  deleteOrganization: (orgId) => api.delete(`/admin/organizations/${orgId}`),
  updateOrgFeatures: (orgId, features) => api.patch(`/admin/organizations/${orgId}/features`, features),

  // Module management
  getModules: () => api.get('/admin/modules'),
  updateModuleConfig: (moduleId, updates) => api.patch(`/admin/modules/${moduleId}`, updates),

  // Per-org module access management
  getOrgModules: (orgId) => api.get(`/admin/organizations/${orgId}/modules`),
  setOrgModules: (orgId, moduleIds) => api.put(`/admin/organizations/${orgId}/modules`, { module_ids: moduleIds }),
  toggleOrgModule: (orgId, moduleId, enabled) => api.patch(`/admin/organizations/${orgId}/modules/${moduleId}`, { enabled }),

  // AI Usage management
  getAIUsageAll: () => api.get('/permit-playbooks/usage/all'),
  setAIUsageLimit: (organizationId, monthlyTokenLimit, isBlocked) =>
    api.post('/permit-playbooks/usage/limits', {
      organization_id: organizationId,
      monthly_token_limit: monthlyTokenLimit,
      is_blocked: isBlocked
    }),

  // System Configuration (AI Providers, etc.)
  getSystemConfig: () => api.get('/admin/config'),
  updateSystemConfig: (updates) => api.patch('/admin/config', updates),

  // Contact Inquiries
  getInquiries: (limit = 100) => api.get('/admin/inquiries', { params: { limit } }),
  updateInquiry: (id, updates) => api.patch(`/admin/inquiries/${id}`, updates),

  // Membership management
  getOrgMemberships: (orgId, includeInactive = true) => api.get(`/admin/organizations/${orgId}/memberships`, { params: { include_inactive: includeInactive } }),
  reactivateMembership: (orgId, userId, role = null) => api.post(`/admin/organizations/${orgId}/memberships/${userId}/reactivate`, role ? { role } : {}),

  // Exactix API Monitor
  getExactixMonitor: () => api.get('/admin/exactix-monitor'),

  // GeoParquet source cache monitor
  getGeoParquetStatus: () => api.get('/admin/geoparquet-status'),
};

// Playbook endpoints
export const playbookAPI = {
  getOrgPlaybooks: (orgId) =>
    api.get(`/permit-playbooks/organizations/${orgId}/playbooks`),

  getOrgPlaybookDetail: (orgId, playbookId, includeSources = true) =>
    api.get(`/permit-playbooks/organizations/${orgId}/playbooks/${playbookId}`, {
      params: { include_sources: includeSources }
    }),

  saveOrgOverride: (orgId, playbookId, overrideJson) =>
    api.put(`/permit-playbooks/organizations/${orgId}/playbooks/${playbookId}/override`, {
      override_json: overrideJson
    }),

  verifyPlaybook: (orgId, playbookId, verificationNotes) =>
    api.post(`/permit-playbooks/organizations/${orgId}/playbooks/${playbookId}/verify`, {
      verification_notes: verificationNotes
    }),

  getPlaybookHistory: (orgId, playbookId) =>
    api.get(`/permit-playbooks/organizations/${orgId}/playbooks/${playbookId}/history`),
};

// Subsurface 3D Viewer endpoints (feature-flagged)
export const subsurfaceViewerAPI = {
  // Check whether the 3D corridor viewer feature is enabled
  isEnabled: () =>
    api.get('/subsurface/viewer/enabled'),

  // Get segment data formatted for viewer selection overlay
  getSegments: (jobId) =>
    api.get('/subsurface/viewer/segments', { params: { job_id: jobId } }),

  // Generate corridor data from selected segments
  generateCorridor: (jobId, segmentIds, sampleInterval = 10, renderDepth = 30) =>
    api.post('/subsurface/viewer/corridor', {
      job_id: jobId,
      segment_ids: segmentIds,
      sample_interval: sampleInterval,
      render_depth: renderDepth,
    }),
};

// Katapult Pro Integration endpoints
export const katapultAPI = {
  // Test API key
  testConnection: (apiKey) =>
    api.post('/katapult/test-connection', { api_key: apiKey }),

  // List available jobs (pass orgId to use stored key)
  listJobs: (apiKey, includeArchived = false, orgId = null) => {
    const params = { include_archived: includeArchived };
    if (apiKey) params.api_key = apiKey;
    if (orgId) params.org_id = orgId;
    return api.get('/katapult/jobs', { params });
  },

  // Link a Katapult job to a project (apiKey optional if org has stored key)
  linkJob: (projectId, katapultJobId, apiKey = null) =>
    api.post(`/katapult/projects/${projectId}/link-job`, {
      katapult_job_id: katapultJobId,
      ...(apiKey ? { api_key: apiKey } : {}),
    }),

  // Trigger sync (uses stored API key) — all jobs
  syncData: (projectId) =>
    api.post(`/katapult/projects/${projectId}/sync`),

  // Trigger sync for a single job
  syncJob: (projectId, jobDbId) =>
    api.post(`/katapult/projects/${projectId}/jobs/${jobDbId}/sync`),

  // Get synced data
  getData: (projectId) =>
    api.get(`/katapult/projects/${projectId}/katapult-data`),

  // Get link/sync status
  getStatus: (projectId) =>
    api.get(`/katapult/projects/${projectId}/katapult-status`),

  // Unlink all jobs and remove data
  unlink: (projectId) =>
    api.delete(`/katapult/projects/${projectId}/unlink`),

  // Unlink a single job
  unlinkJob: (projectId, jobDbId) =>
    api.delete(`/katapult/projects/${projectId}/jobs/${jobDbId}/unlink`),

  // Get photo metadata
  getPhoto: (projectId, photoId) =>
    api.get(`/katapult/projects/${projectId}/photos/${photoId}`),

  // Get GeoJSON for map rendering (nodes as Points, connections as LineStrings)
  getGeoJSON: (projectId) =>
    api.get(`/katapult/projects/${projectId}/katapult-geojson`),

  // On-demand photo URL proxy (fetches from Katapult and caches)
  getPhotoUrl: (projectId, photoId) =>
    api.get(`/katapult/projects/${projectId}/photos/${photoId}/url`),

  // Convert Katapult data to a versioned project asset
  saveAsAsset: (projectId, assetType = 'poles', name = 'Katapult Import') =>
    api.post(`/katapult/projects/${projectId}/katapult-to-asset`, {
      asset_type: assetType,
      name,
    }),

  // Org-level key management
  getOrgKeyStatus: (orgId) =>
    api.get(`/katapult/org/${orgId}/key-status`),
  saveOrgKey: (orgId, apiKey) =>
    api.post(`/katapult/org/${orgId}/save-key`, { api_key: apiKey }),
  removeOrgKey: (orgId) =>
    api.delete(`/katapult/org/${orgId}/remove-key`),
  testOrgKey: (orgId) =>
    api.post(`/katapult/org/${orgId}/test-key`),

  // Photo measurements
  getPhotoMeasurements: (projectId, photoId) =>
    api.get(`/katapult/projects/${projectId}/photos/${photoId}/measurements`),
  saveCalibration: (projectId, photoId, data) =>
    api.put(`/katapult/projects/${projectId}/photos/${photoId}/calibration`, data),
  addMeasurement: (projectId, photoId, data) =>
    api.post(`/katapult/projects/${projectId}/photos/${photoId}/measurements`, data),
  deleteMeasurement: (projectId, photoId, measurementId) =>
    api.delete(`/katapult/projects/${projectId}/photos/${photoId}/measurements/${measurementId}`),
  updateMeasurement: (projectId, photoId, measurementId, data) =>
    api.patch(`/katapult/projects/${projectId}/photos/${photoId}/measurements/${measurementId}`, data),
  setMainPhoto: (projectId, photoId, isMain) =>
    api.patch(`/katapult/projects/${projectId}/photos/${photoId}/set-main`, { is_main: isMain }),

  // Attachment owners (org-level picklist)
  getAttachmentOwners: (orgId) =>
    api.get(`/katapult/org/${orgId}/attachment-owners`),
  createAttachmentOwner: (orgId, name) =>
    api.post(`/katapult/org/${orgId}/attachment-owners`, { name }),
  deleteAttachmentOwner: (orgId, ownerId) =>
    api.delete(`/katapult/org/${orgId}/attachment-owners/${ownerId}`),
};

// Fielding (internal field data collection) endpoints
export const fieldingAPI = {
  // Nodes
  listNodes: (projectId) =>
    api.get(`/fielding/projects/${projectId}/nodes`),
  createNode: (projectId, data) =>
    api.post(`/fielding/projects/${projectId}/nodes`, data),
  updateNode: (projectId, nodeId, data) =>
    api.put(`/fielding/projects/${projectId}/nodes/${nodeId}`, data),
  deleteNode: (projectId, nodeId) =>
    api.delete(`/fielding/projects/${projectId}/nodes/${nodeId}`),

  // Connections
  listConnections: (projectId) =>
    api.get(`/fielding/projects/${projectId}/connections`),
  createConnection: (projectId, data) =>
    api.post(`/fielding/projects/${projectId}/connections`, data),
  deleteConnection: (projectId, connId) =>
    api.delete(`/fielding/projects/${projectId}/connections/${connId}`),

  // Photos (support both node_id and asset_id+feature_index linking)
  listPhotos: (projectId, { nodeId = null, assetId = null, featureIndex = null } = {}) =>
    api.get(`/fielding/projects/${projectId}/photos`, {
      params: {
        ...(nodeId ? { node_id: nodeId } : {}),
        ...(assetId ? { asset_id: assetId } : {}),
        ...(featureIndex !== null && featureIndex !== undefined ? { feature_index: featureIndex } : {}),
      }
    }),
  createPhoto: (projectId, data) =>
    api.post(`/fielding/projects/${projectId}/photos`, data),
  uploadPhoto: (projectId, file, { nodeId = null, assetId = null, featureIndex = null, notes = null } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (nodeId) formData.append('node_id', nodeId);
    if (assetId) formData.append('asset_id', assetId);
    if (featureIndex !== null && featureIndex !== undefined) formData.append('feature_index', featureIndex);
    if (notes) formData.append('notes', notes);
    return api.post(`/fielding/projects/${projectId}/photos/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (projectId, photoId) =>
    api.delete(`/fielding/projects/${projectId}/photos/${photoId}`),

  // Photo URL (direct serve endpoint — full URL so <img src> reaches backend)
  getPhotoUrl: (projectId, photoId) =>
    Promise.resolve({ data: { url: `${API_BASE_URL}/api/v1/fielding/projects/${projectId}/photos/${photoId}/file` } }),

  // Measurement CRUD (mirrors katapultAPI shape)
  getPhotoMeasurements: (projectId, photoId) =>
    api.get(`/fielding/projects/${projectId}/photos/${photoId}/measurements`),
  saveCalibration: (projectId, photoId, data) =>
    api.put(`/fielding/projects/${projectId}/photos/${photoId}/calibration`, data),
  addMeasurement: (projectId, photoId, data) =>
    api.post(`/fielding/projects/${projectId}/photos/${photoId}/measurements`, data),
  deleteMeasurement: (projectId, photoId, measurementId) =>
    api.delete(`/fielding/projects/${projectId}/photos/${photoId}/measurements/${measurementId}`),
  updateMeasurement: (projectId, photoId, measurementId, data) =>
    api.patch(`/fielding/projects/${projectId}/photos/${photoId}/measurements/${measurementId}`, data),

  // GeoJSON export (for map overlay)
  getGeoJSON: (projectId) =>
    api.get(`/fielding/projects/${projectId}/geojson`),

  // Import Katapult nodes as field assets
  importKatapult: (projectId) =>
    api.post(`/fielding/projects/${projectId}/import-katapult`),
};

export default api;

// NEPA BEAD Compliance Hub endpoints
export const nepaAPI = {
  // Hub — cross-project listing
  listReviews: (params) => api.get('/nepa/reviews', { params }),
  getStats: (orgId) => api.get('/nepa/stats', { params: orgId ? { organization_id: orgId } : {} }),

  // Review CRUD
  getReview: (id) => api.get(`/nepa/reviews/${id}`),
  getReviewSummary: (id) => cachedGet(`/nepa/reviews/${id}/detail-summary`),
  updateReview: (id, data) => api.patch(`/nepa/reviews/${id}`, data),

  // Workflow
  getWorkflow: (reviewId) => api.get(`/nepa/reviews/${reviewId}/workflow`),

  // Tasks
  updateTaskStatus: (taskId, data) => api.patch(`/nepa/tasks/${taskId}/status`, data),
  assignTask: (taskId, userId) => api.patch(`/nepa/tasks/${taskId}/assign`, null, { params: { user_id: userId } }),

  // Checklist
  updateChecklist: (itemId, data) => api.patch(`/nepa/checklist/${itemId}`, data),

  // ESAPTT
  getEsaptt: (reviewId) => api.get(`/nepa/reviews/${reviewId}/esaptt`),
  updateEsaptt: (subId, data) => api.patch(`/nepa/esaptt/${subId}`, data),

  // Per-project dashboard
  getDashboard: (projectId) => api.get(`/nepa/dashboard/project/${projectId}`),

  // Decision Memo
  generateMemo: (reviewId) => api.post(`/nepa/reviews/${reviewId}/generate-memo`),

  // NTIA Resources
  getResources: (reviewId) => cachedGet(`/nepa/reviews/${reviewId}/resources`),

  // ESA §7 / NHPA §106 Fast-Track Evaluations
  evaluateEsa: (reviewId) => api.get(`/nepa/reviews/${reviewId}/esa-evaluation`),
  evaluateNhpa: (reviewId) => api.get(`/nepa/reviews/${reviewId}/nhpa-evaluation`),

  // Map Data (corridor + finding layers for map rendering)
  getMapData: (reviewId) => cachedGet(`/nepa/reviews/${reviewId}/map-data`),
};


// Locate Ticket Scraper endpoints
export const locateAPI = {
  getJobs: (orgId) => {
    // Guard: if an org object was accidentally passed, extract its id
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    if (!resolvedId) return Promise.resolve({ data: [] });
    return api.get('/locate/jobs', { params: { org_id: resolvedId } });
  },
  createJob: (jobData) => api.post('/locate/jobs', jobData),
  updateJob: (jobId, jobData) => api.put(`/locate/jobs/${jobId}`, jobData),
  deleteJob: (jobId) => api.delete(`/locate/jobs/${jobId}`),
  runJobNow: (jobId) => api.post(`/locate/jobs/${jobId}/run`),
  cancelJobRun: (jobId) => api.post(`/locate/jobs/${jobId}/cancel`),
  refreshTicket: (jobId, ticketId) => api.post(`/locate/jobs/${jobId}/refresh-ticket/${ticketId}`),
  updateTicketTracking: (jobId, ticketId, action) => api.post(`/locate/jobs/${jobId}/tickets/${ticketId}/tracking`, { action }),
  setTicketsWorkComplete: (jobId, ticketIds, completed = true) => api.post(`/locate/jobs/${jobId}/tickets/work-complete`, {
    ticket_ids: ticketIds,
    completed,
  }),
  getJobStatus: (jobId) => api.get(`/locate/jobs/${jobId}/status`),
  provisionLayer: (jobId) => api.post(`/locate/jobs/${jobId}/provision-layer`),
  getRuns: ({ jobId = null, orgId = null, limit = null } = {}) => {
    const resolvedOrgId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    return api.get('/locate/runs', {
      params: {
        ...(jobId ? { job_id: jobId } : {}),
        ...(resolvedOrgId ? { org_id: resolvedOrgId } : {}),
        ...(limit ? { limit } : {})
      }
    });
  },
  getCumulative: (orgId) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    if (!resolvedId) return Promise.resolve({ data: { type: 'FeatureCollection', features: [] } });
    return api.get('/locate/cumulative', { params: { org_id: resolvedId } });
  },
  downloadPrivateLocates: (orgId, format = 'shp', jobId = null) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    return api.get(`/locate/private-locates/download/${format}`, {
      params: {
        org_id: resolvedId,
        ...(jobId ? { job_id: jobId } : {})
      },
      responseType: 'blob'
    });
  },
  exportSelectedTickets: async (orgId, tickets) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    const csrfToken = await ensureCsrfToken();
    return api.post('/locate/tickets/export/shapefile', {
      organization_id: resolvedId,
      tickets
    }, {
      responseType: 'blob',
      headers: csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}
    });
  },
  getRunGeojson: (runId) => api.get(`/locate/runs/${runId}/geojson`),
  getAnalytics: (orgId, period = 'day', rangeDays = 90) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    if (!resolvedId) return Promise.resolve({ data: { closures: [], summary: {}, slowest_utilities: [] } });
    return api.get('/locate/analytics', { params: { org_id: resolvedId, period, range_days: rangeDays } });
  },
  getMorningReport: (orgId) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    return api.get('/locate/reports/morning', { params: { org_id: resolvedId } });
  },
  sendMorningReport: (orgId, { recipients = null, use_default_recipients = null, force = true } = {}) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    return api.post('/locate/reports/morning/send', { recipients, use_default_recipients, force }, { params: { org_id: resolvedId } });
  },
  getReportSettings: (orgId) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    return api.get('/locate/reports/settings', { params: { org_id: resolvedId } });
  },
  saveReportSettings: (orgId, settings) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    return api.put('/locate/reports/settings', settings, { params: { org_id: resolvedId } });
  },
  getUtilityReport: (orgId, company) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    return api.get('/locate/analytics/utility-report', { params: { org_id: resolvedId, company } });
  },
  getStatusConfig: (orgId) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    if (!resolvedId) return Promise.resolve({ data: { statuses: {}, unreviewed_count: 0, total_unique: 0 } });
    return api.get('/locate/status-config', { params: { org_id: resolvedId } });
  },
  saveStatusConfig: (orgId, statuses) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    return api.put('/locate/status-config', { statuses }, { params: { org_id: resolvedId } });
  },
  bulkCreateTickets: (jobId, orgId, features) => {
    const resolvedId = orgId && typeof orgId === 'object' ? orgId.id : orgId;
    return api.post(`/locate/jobs/${jobId}/bulk-create-tickets`, {
      organization_id: resolvedId,
      features
    });
  },

  // --- Design Upload ---
  previewDesign: (jobId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/locate/jobs/${jobId}/designs/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadDesign: (jobId, file, selectedLayers = null, designName = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (selectedLayers && selectedLayers.length > 0) {
      formData.append('selected_layers', JSON.stringify(selectedLayers));
    }
    if (designName) {
      formData.append('name', designName);
    }
    return api.post(`/locate/jobs/${jobId}/designs`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getDesigns: (jobId) => api.get(`/locate/jobs/${jobId}/designs`),
  getDesign: (jobId, designId) => api.get(`/locate/jobs/${jobId}/designs/${designId}`),
  deleteDesign: (jobId, designId) => api.delete(`/locate/jobs/${jobId}/designs/${designId}`),

  // --- Draft Tickets ---
  createDraftTickets: (jobId, polygon, designId, reverseGeocode = false, ticketLengthFt = 500) =>
    api.post(`/locate/jobs/${jobId}/draft-tickets`, {
      polygon,
      design_id: designId,
      reverse_geocode: reverseGeocode,
      ticket_length_ft: ticketLengthFt,
    }),
  clearDraftTickets: (jobId) => api.delete(`/locate/jobs/${jobId}/draft-tickets`),
  deleteDraftTicket: (jobId, ticketId) => api.delete(`/locate/jobs/${jobId}/draft-tickets/${ticketId}`),
};

// Custom Tracking Module endpoints
export const trackerAPI = {
  // Tracker CRUD
  list: () => cachedGet('/trackers/'),
  getGlobalActivity: (limit = 50) => api.get('/trackers/activity', { params: { limit } }),
  getGlobalMapData: () => cachedGet('/trackers/map-data'),
  getDashboardConfig: () => api.get('/trackers/dashboard/config'),
  saveDashboardConfig: (charts) => api.put('/trackers/dashboard/config', charts),
  aggregate: (body) => api.post('/trackers/aggregate', body),
  computeIndicator: (body) => api.post('/trackers/indicator', body),
  create: (data) => api.post('/trackers/', data),
  get: (trackerId, includeRows = false) =>
    cachedGet(`/trackers/${trackerId}`, { params: { include_rows: includeRows } }),
  update: (trackerId, data) => api.put(`/trackers/${trackerId}`, data),
  delete: (trackerId) => api.delete(`/trackers/${trackerId}`),

  // Field CRUD
  listFields: (trackerId) => cachedGet(`/trackers/${trackerId}/fields`),
  createField: (trackerId, data) => api.post(`/trackers/${trackerId}/fields`, data),
  updateField: (trackerId, fieldId, data) =>
    api.put(`/trackers/${trackerId}/fields/${fieldId}`, data),
  deleteField: (trackerId, fieldId) =>
    api.delete(`/trackers/${trackerId}/fields/${fieldId}`),
  reorderFields: (trackerId, fieldIds) =>
    api.patch(`/trackers/${trackerId}/fields/reorder`, { field_ids: fieldIds }),

  // Row CRUD
  listRows: (trackerId, params = {}) =>
    api.get(`/trackers/${trackerId}/rows`, { params }),
  createRow: (trackerId, values) =>
    api.post(`/trackers/${trackerId}/rows`, { values }),
  updateRow: (trackerId, rowId, values, actionLabel) =>
    api.put(`/trackers/${trackerId}/rows/${rowId}`, { values }, actionLabel ? { params: { action_label: actionLabel } } : undefined),
  deleteRow: (trackerId, rowId) =>
    api.delete(`/trackers/${trackerId}/rows/${rowId}`),
  getRowHistory: (trackerId, rowId) =>
    api.get(`/trackers/${trackerId}/rows/${rowId}/history`),
  manualLocate: (trackerId, rowId, data) =>
    api.post(`/trackers/${trackerId}/rows/${rowId}/manual-locate`, data),
  getRowsGeoJSON: (trackerId) =>
    cachedGet(`/trackers/${trackerId}/rows/geojson`),

  // Image field uploads
  uploadFieldImage: (trackerId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/trackers/${trackerId}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadPublicFormImage: (token, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/forms/${token}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // Form CRUD
  listForms: (trackerId) => cachedGet(`/trackers/${trackerId}/forms`),
  
  // Row Comments
  listRowComments: (trackerId, rowId) => 
    api.get(`/trackers/${trackerId}/rows/${rowId}/comments`),
  createRowComment: (trackerId, rowId, content) =>
    api.post(`/trackers/${trackerId}/rows/${rowId}/comments`, { content }),
    
  // Row Attachments
  listRowAttachments: (trackerId, rowId) =>
    api.get(`/trackers/${trackerId}/rows/${rowId}/attachments`),
  uploadRowAttachment: (trackerId, rowId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/trackers/${trackerId}/rows/${rowId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteRowAttachment: (trackerId, rowId, attachmentId) =>
    api.delete(`/trackers/${trackerId}/rows/${rowId}/attachments/${attachmentId}`),
  createForm: (trackerId, data) =>
    api.post(`/trackers/${trackerId}/forms`, data),
  updateForm: (trackerId, formId, data) =>
    api.put(`/trackers/${trackerId}/forms/${formId}`, data),
  deleteForm: (trackerId, formId) =>
    api.delete(`/trackers/${trackerId}/forms/${formId}`),

  // Public form (no auth)
  getPublicForm: (token) => api.get(`/forms/${token}`),
  submitPublicForm: (token, values) =>
    api.post(`/forms/${token}/submit`, { values }),
};
